import { TextLayer } from "@deck.gl/layers";
import { clamp, easeOutBack } from "lib/helpers/math";
import context from "lib/stores/context";
import currencyToString from "lib/helpers/currencyToString";
import apiData from "lib/stores/apiData";
import mapData from "lib/stores/mapData";

function isOverlapping(label1, label2, textSize, padding = [9, 7, 9, 6]) {
  const x1 = label1.coordinates[0] + label1.pixelOffset[0];
  const y1 = label1.coordinates[1] + label1.pixelOffset[1];
  const x2 = label2.coordinates[0] + label2.pixelOffset[0];
  const y2 = label2.coordinates[1] + label2.pixelOffset[1];

  const paddingX = Math.max(padding[0], padding[2]);
  const paddingY = Math.max(padding[1], padding[3]);

  return Math.abs(x1 - x2) < textSize + paddingX &&
    Math.abs(y1 - y2) < textSize + paddingY;
}
function resolveOverlapping(labels, textSize) {
  let offsetAdjustment = textSize;
  let hasOverlap = true;
  while (hasOverlap) {
    hasOverlap = false;
    for (let i = 0; i < labels.length; i++) {
      for (let j = i + 1; j < labels.length; j++) {
        if (isOverlapping(labels[i], labels[j], textSize)) {
          labels[j].pixelOffset[0] += clamp(offsetAdjustment, 0, textSize + 9);
          labels[j].pixelOffset[1] += clamp(offsetAdjustment, 0, textSize + 7);
          labels[i].pixelOffset[0] -= clamp(offsetAdjustment, 0, textSize + 9);
          labels[i].pixelOffset[1] -= clamp(offsetAdjustment, 0, textSize + 7);
          hasOverlap = true;
        }
      }
    }
  }
}

const setCountryLabelsLayer = (productsCountry, selectedProduct, viewState) => {
  const getImportedValueLabel = () => {
    try {
      if (!productsCountry || !selectedProduct) return null;
      const country = apiData.countries[productsCountry];
      const coordinates = [
        apiData.countries[productsCountry].centroid.cx,
        apiData.countries[productsCountry].centroid.cy,
      ];
      return {
        text: `US${getTotalImportValue()} imported by ${country.name}`,
        coordinates,
        pixelOffset: [0, 0],
      };
    } catch (error) {
      return null;
    }
  };

  const getLabels = () => {
    const importedValueLabel = getImportedValueLabel();
    const textLayers = getTextLayers();
    if (importedValueLabel) {
      textLayers.unshift(importedValueLabel);
    }
    return textLayers;
  };

  const getTotalImportValue = () => {
    const data = apiData.countries[productsCountry];
    let tally = 0;
    data.imports.forEach((d) => {
      if (d.product.toLowerCase().replaceAll(' ', '%20') === selectedProduct) {
        tally += parseFloat(d.value);
      }
    });
    return currencyToString(Math.floor(tally), {});
  };

  const getTextLayers = () => {
    if (!productsCountry || !selectedProduct) return [];
    const returnData = [];
    const data = apiData.countries[productsCountry];
    data.imports.forEach((d) => {
      const exportingCountry = Object.keys(apiData.countries).find(
        (country) => {
          return apiData.countries[country].iso === d.exporter;
        },
      );
      if (exportingCountry && d.product.toLowerCase().replaceAll(' ', '%20') === selectedProduct) {
        returnData.push({
          coordinates: [
            apiData.countries[exportingCountry].centroid.cx,
            apiData.countries[exportingCountry].centroid.cy,
          ],
          text: getExporterLabel(d.exporter, d.value, d.value_locale),
          pixelOffset: [0, 0],
        });
      }
    });
    return returnData;
  };

  const getExporterLabel = (countryName, exportValue, localeValue) => {
    return `US${localeValue} from ${apiData.countries[countryName].name}`;
  };

  const getSize = () => {
    if (viewState.zoom <= 3) {
      return 12;
    } else if (viewState.zoom >= 3 && viewState.zoom <= 5) {
      return 14;
    } else if (viewState.zoom > 5) {
      return 16;
    }
  };

  const CUSTOM_CHARSET =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÔÜabcdefghijklmnopqrstuvwxyzäöôüß0123456789!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~ ";

  const labels = getLabels();
  resolveOverlapping(labels, getSize());

  return new TextLayer({
    id: "text-layer",
    data: labels,
    pickable: false,
    getPosition: (data) => data.coordinates,
    getText: (data) => data.text ?? null,
    getSize,
    background: true,
    parameters: {
      depthTest: false,
    },
    getBackgroundColor: [0, 0, 40, 229.5],
    getColor: (info, object) => {
      return [255, 255, 255];
    },
    backgroundPadding: [7, 5, 7, 4],
    fontFamily: "Source Sans Pro",
    fontWeight: 600,
    sizeUnits: "pixels",
    getAngle: 0,
    getTextAnchor: "middle",
    getAlignmentBaseline: "center",
    billboard: true,
    characterSet: CUSTOM_CHARSET,
    getPixelOffset: (d) => {
      return d.pixelOffset;
    },
    getPolygonOffset: ({ layerIndex }) => [-4000, 100],
    updateTrigger: {
      data: [productsCountry, selectedProduct],
      viewState: viewState.zoom,
      getSize: viewState.zoom,
      getPixelOffset: labels,
    },
  });
};

export { setCountryLabelsLayer };
